// `defineSetting` — the settings registry primitive (spec/03-architecture.md §9).
//
// "Configure, don't fork." Every behaviour that differs between venues is a setting declared once,
// here, with a default that preserves current behaviour. A setting that changes what existing
// venues experience the day it ships is a bug (CLAUDE.md).
//
//     defineSetting({
//       key: 'sessions.offlinePolicy',
//       type: z.enum(['lock_immediately', 'continue_to_lease_end', 'continue_and_settle']),
//       default: 'continue_to_lease_end',
//       scopes: ['platform', 'organization', 'venue'],
//       visibleTo: 'venue',
//       group: 'sessions',
//       lockableAt: [],
//       editPermission: 'settings:sessions',
//       entitlement: null,
//       description: 'What stations do when they cannot reach the cloud',
//     })
//
// `group` and `lockableAt` are the two fields beyond spec/03 §9's first example (F-ORG-02): the
// heading the generated settings screen shows the setting under (spec/13 §13, `groups.ts`), so a new
// setting appears in its console group with nothing but this entry; and where HQ may lock it, as its
// owning document's settings table says (`[]`: never).
//
// The registry is deliberately empty here. Each feature declares the settings it introduces, in
// its own module, and registers them; a central list of every setting in the product is a file
// every pull request would have to touch.
import { z } from "../zod.js";
import { PERMISSION_PATTERN } from "../primitives/scalars.js";
import { isSettingGroup } from "./groups.js";
import { isSettingUnit, SETTING_UNIT_KEYWORD } from "./units.js";
/**
 * Where a value may be set. Resolution runs from the most specific to the least:
 * station → station class → zone → venue → organisation → platform default (spec/03 §9).
 * Not every setting accepts every scope; `scopes` says which.
 */
export const SETTING_SCOPES = ['platform', 'organization', 'venue', 'zone', 'stationClass', 'station'];
/** Most specific first. A resolver walks this order and takes the first value it finds. */
export const SETTING_RESOLUTION_ORDER = [
    'station',
    'stationClass',
    'zone',
    'venue',
    'organization',
    'platform',
];
/**
 * Which settings screen shows it: HQ's, the organisation's, or the venue's (spec/13 §13).
 *
 * It names the **lowest** screen that sees the setting. A screen shows what is visible to it and
 * to every screen below it: HQ's platform screen shows every setting, an organisation's shows the
 * `organization` and `venue` ones, and a venue's the `venue` ones only. So `visibleTo: 'platform'`
 * — every feature flag — is HQ's alone, and a venue never finds it in its own screen
 * (`settingVisibleAt`).
 */
export const SETTING_AUDIENCES = ['platform', 'organization', 'venue'];
/** The screens that see a setting visible to `visibleTo`. */
const AUDIENCES_THAT_SEE = {
    platform: ['platform'],
    organization: ['platform', 'organization'],
    venue: ['platform', 'organization', 'venue'],
};
/** Does the screen `audience` show this setting (and so let its editors change it)? */
export function settingVisibleAt(definition, audience) {
    return AUDIENCES_THAT_SEE[definition.visibleTo].includes(audience);
}
/**
 * Where HQ can **lock** a setting so that no scope below it can override it (spec/03 §9): at
 * platform scope, which binds every organisation and venue, and at one organisation's scope, which
 * binds its venues and everything inside them. A lock is a property of the value stored at that
 * scope — the locked value is the value — so a setting is locked only at a scope it accepts.
 *
 * Which settings may be locked, and where, is the specification's to say, not HQ's to decide per
 * change: each owning document's "Settings introduced here" table notes it in the Scopes column
 * ("HQ may lock", "locked max", "HQ per org (locked)", "never locked"), and a setting declares the
 * same thing as `lockableAt`. A setting with no note there is never locked (`lockableAt: []`).
 */
export const SETTING_LOCK_SCOPES = ['platform', 'organization'];
/** The scopes a lock at each lock scope binds. A lock with none of them accepted would bind nothing. */
export const SCOPES_BELOW_LOCK = {
    platform: ['organization', 'venue', 'zone', 'stationClass', 'station'],
    organization: ['venue', 'zone', 'stationClass', 'station'],
};
/** `namespace.camelCaseName`, at least two segments: `sessions.offlinePolicy`. */
export const SETTING_KEY_PATTERN = /^[a-z][a-z0-9]*(?:\.[a-z][a-zA-Z0-9]*)+$/;
export class SettingDefinitionError extends Error {
    name = 'SettingDefinitionError';
}
/**
 * Declare a setting. Returns the definition, typed, so `z.infer` of its `type` is the value type
 * a resolver hands back.
 *
 * Everything that can be checked at declaration time is: the key's shape, that the scopes are
 * known, distinct and non-empty, that the audience and the group are known, that each lock scope is
 * one the setting accepts with a scope below it to bind, that the permission looks like
 * `resource:action`, and — the one that matters — that the default is a value the schema accepts.
 * A default that does not parse is a venue getting a validation error on a screen it never opened.
 */
export function defineSetting(definition) {
    const { key, type, scopes, visibleTo, group, lockableAt, editPermission } = definition;
    if (!SETTING_KEY_PATTERN.test(key)) {
        throw new SettingDefinitionError(`Setting key "${key}" must look like "namespace.settingName".`);
    }
    if (scopes.length === 0) {
        throw new SettingDefinitionError(`Setting "${key}" must accept at least one scope.`);
    }
    if (new Set(scopes).size !== scopes.length) {
        throw new SettingDefinitionError(`Setting "${key}" lists a scope twice.`);
    }
    for (const scope of scopes) {
        if (!SETTING_SCOPES.includes(scope)) {
            throw new SettingDefinitionError(`Setting "${key}" has unknown scope "${scope}".`);
        }
    }
    if (!SETTING_AUDIENCES.includes(visibleTo)) {
        throw new SettingDefinitionError(`Setting "${key}" has unknown visibleTo "${visibleTo}".`);
    }
    if (!isSettingGroup(group)) {
        throw new SettingDefinitionError(`Setting "${key}" has unknown group "${String(group)}"; the groups are in groups.ts.`);
    }
    if (new Set(lockableAt).size !== lockableAt.length) {
        throw new SettingDefinitionError(`Setting "${key}" lists a lock scope twice.`);
    }
    for (const scope of lockableAt) {
        if (!SETTING_LOCK_SCOPES.includes(scope)) {
            throw new SettingDefinitionError(`Setting "${key}" can be locked only at platform or organisation scope, not "${scope}".`);
        }
        if (!scopes.includes(scope)) {
            throw new SettingDefinitionError(`Setting "${key}" is lockable at ${scope} scope but does not accept that scope.`);
        }
        if (!SCOPES_BELOW_LOCK[scope].some((below) => scopes.includes(below))) {
            throw new SettingDefinitionError(`Setting "${key}" is lockable at ${scope} scope but accepts no scope below it to bind.`);
        }
    }
    if (!PERMISSION_PATTERN.test(editPermission)) {
        throw new SettingDefinitionError(`Setting "${key}" needs an editPermission like "settings:sessions".`);
    }
    // A dated setting is never locked (spec/03 §9, spec/06 §11): HQ cannot lock a venue's tax mode,
    // because a registration is a fact about the business, not a policy anyone above it chooses. Its
    // values live in the owning module's table, not `setting_values`, so a lock stored there would bind
    // nothing anyway.
    if (definition.dated === true && lockableAt.length > 0) {
        throw new SettingDefinitionError(`Setting "${key}" is dated, so it cannot be lockable.`);
    }
    if (definition.unit !== undefined) {
        if (!isSettingUnit(definition.unit)) {
            throw new SettingDefinitionError(`Setting "${key}" has unknown unit "${String(definition.unit)}".`);
        }
        // Money is integer minor units (spec/06 §2): a unit on anything but an integer would have the
        // screen convert a value that is not an amount.
        if (!isIntegerSchema(z.toJSONSchema(type, { io: 'input', unrepresentable: 'any' }))) {
            throw new SettingDefinitionError(`Setting "${key}" has unit "${definition.unit}", which needs an integer type.`);
        }
    }
    const parsed = type.safeParse(definition.default);
    if (!parsed.success) {
        throw new SettingDefinitionError(`Setting "${key}" has a default its own schema rejects: ${z.prettifyError(parsed.error)}`);
    }
    return definition;
}
/** An integer JSON Schema, or an integer-or-null one. */
function isIntegerSchema(schema) {
    if (schema['type'] === 'integer')
        return true;
    const members = schema['anyOf'];
    if (!Array.isArray(members))
        return false;
    const records = members;
    const others = records.filter((member) => member['type'] !== 'null');
    return others.length === 1 && others[0]?.['type'] === 'integer';
}
/**
 * Mark a field inside a structured setting with a display unit (`units.ts`): a top-up offer's
 * `minTopupAmount: settingUnit(z.int().min(0), 'money')`. It adds the `x-clavian-unit` keyword to the
 * field's JSON Schema, which the settings screen reads at any depth; what the field accepts does not
 * change. A setting whose whole value is the amount declares `unit` instead.
 */
export function settingUnit(schema, unit) {
    return schema.meta({ [SETTING_UNIT_KEYWORD]: unit });
}
/**
 * Declare a module's settings in one call. The result is keyed by setting key, so a module can
 * reach its own settings by name with the value type intact.
 */
export function defineSettings(...definitions) {
    const module = {};
    for (const definition of definitions) {
        if (module[definition.key] !== undefined) {
            throw new SettingDefinitionError(`Setting "${definition.key}" is declared twice in the same module.`);
        }
        module[definition.key] = definition;
    }
    return module;
}
//# sourceMappingURL=define-setting.js.map